/**
 * @file auth.service.ts
 * @version 2.2.0
 * @datetime 2026-09-06T02:05:00+03:00
 * @description Unified Authentication Service with isolated pre-auth gate, deep Firebase auth cleanup on logout, and PouchDB preservation.
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { environment } from '../../environments/environment';
import { SocketService } from './socket.service';
import { DatabaseService } from './database.service';
import { GoogleWorkspaceService } from './google-workspace.service';

export interface User {
  name: string;
  phone: string;
  type: string;
  vkn?: string;
  vknTckn?: string;
  VKN?: string;
  taxid?: string;
  taxId?: string;
  companyName?: string;
  organization?: string;
  plan?: string;
  userRole?: string;
  userType?: string;
  status?: string;
  datasource?: 'own_drive' | 'central_drive' | 'couchdb' | 'duckdb' | string;
  ocr?: 'gemini' | 'python' | 'deepseek' | 'qwen' | 'mistral' | string;
  email?: string;
  id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly version = '2.3.0';
  readonly lastUpdated = '2026-09-12T21:05:00+03:00';

  private readonly router = inject(Router);
  private readonly socketService = inject(SocketService);
  private readonly db = inject(DatabaseService);
  private readonly googleWorkspace = inject(GoogleWorkspaceService);
  private readonly pageLoadTime = typeof window !== 'undefined' ? Date.now() : 0;

  // Firebase initialization with unified firebaseConfig
  private readonly firebaseApp = getApps().length ? getApp() : initializeApp(environment.firebaseConfig);
  readonly firebaseAuth = getAuth(this.firebaseApp);

  private readonly currentUserSignal = signal<User | null>(this.getStoredUser());
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null);

  readonly isLoginModalOpen = signal<boolean>(false);

  /**
   * Reads persistent user session from localStorage if user did not log out.
   */
  private getStoredUser(): User | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const stored = localStorage.getItem('currentUser');
        if (stored) {
          const user = JSON.parse(stored);
          if (user && (user.phone || user.vkn || user.name)) {
            return user;
          }
        }
      } catch (e) {
        console.warn('[AUTH] Error reading persistent user from localStorage:', e);
      }
    }
    return null;
  }

  openLoginModal(): void {
    this.isLoginModalOpen.set(true);
  }

  closeLoginModal(): void {
    this.isLoginModalOpen.set(false);
  }

  setCurrentUser(user: User | null): void {
    // Reset database replication and in-memory caches on every user transition
    this.db.stopSyncAndReset();

    if (typeof window !== 'undefined' && window.localStorage) {
      // Wipe previous residual keys from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (
          k &&
          (k.startsWith('ustat_mukellefler_') ||
            k === 'ustat_token' ||
            k === 'currentUser' ||
            k === 'user')
        ) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));

      // Save user strictly under currentUser
      if (user) {
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
    }

    this.currentUserSignal.set(user);
  }

  constructor() {
    const user = this.currentUserSignal();
    if (user) {
      console.log(`[AUTH] Session persistent: User "${user.name || user.phone}" is already logged in. Routing directly to main.`);
    } else {
      console.log('[AUTH] No active user session in localStorage. Starting at login screen.');
    }
  }

  /**
   * Triggers the Firebase Phone Number Verification SMS.
   */
  async sendSMSCode(phoneNumber: string, appVerifier: RecaptchaVerifier): Promise<any> {
    console.log('[AUTH] Requesting SMS code for:', phoneNumber);
    return await signInWithPhoneNumber(this.firebaseAuth, phoneNumber, appVerifier);
  }

  /**
   * Confirms the OTP code sent to user phone.
   */
  async verifySMSCode(confirmationResult: any, code: string): Promise<any> {
    console.log('[AUTH] Confirming verification code:', code);
    const result = await confirmationResult.confirm(code);
    return result.user;
  }

  /**
   * Pre-verifies whether the phone exists in the shared server BEFORE triggering SMS.
   * Accurately parses server's status: 'FOUND' and even codeNumber security responses.
   */
  async checkPhoneInDatabase(phone: string): Promise<any> {
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    this.socketService.connect(environment.apiBaseUrl);

    return new Promise((resolve) => {
      let resolved = false;

      const handler = (data: any) => {
        if (!resolved) {
          resolved = true;
          this.socketService.off('check_phone_response', handler);
          const isFound = Boolean(
            data && (
              data.status === 'FOUND' ||
              data.status === 'OK' ||
              Boolean(data.user) ||
              (typeof data.codeNumber === 'number' && data.codeNumber % 2 === 0)
            )
          );
          const isBlocked = data?.status === 'REJECT' || data?.status === 'BLOCKED';
          resolve({
            ...data,
            userFound: isFound,
            status: isBlocked ? 'BLOCKED' : (isFound ? 'OK' : 'NOT_FOUND'),
          });
        }
      };

      this.socketService.on('check_phone_response', handler);

      this.socketService.emit('check_phone', {
        phone: cleanPhone,
        browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'Mobile',
        telemetry: {
          platform: typeof navigator !== 'undefined' ? navigator.platform : 'Mobile',
          timeToInteractMs: Date.now() - this.pageLoadTime,
        },
      });

      // Timeout handler: Query CouchDB users table directly; no fake fallback!
      setTimeout(async () => {
        if (!resolved) {
          resolved = true;
          this.socketService.off('check_phone_response', handler);
          try {
            const dbCheck = await this.getUserData(cleanPhone);
            if (dbCheck && dbCheck.user) {
              resolve({ status: 'OK', userFound: true, user: dbCheck.user });
              return;
            }
          } catch (_) {}
          resolve({ status: 'NOT_FOUND', userFound: false, message: 'Bu telefon numarası sistemde kayıtlı değildir.' });
        }
      }, 4000);
    });
  }

  /**
   * Retrieves full CouchDB user profile and sets session via Socket.IO
   */
  async getUserProfileViaSocket(phone: string): Promise<any> {
    this.socketService.connect(environment.apiBaseUrl);
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);

    return new Promise((resolve, reject) => {
      const handler = (data: any) => {
        this.socketService.off('get_user_profile_response', handler);
        if (data && data.status === 'SUCCESS' && data.user) {
          this.setCurrentUser(data.user);
          resolve(data);
        } else {
          reject(new Error(data?.message || 'Profil verisi alınamadı.'));
        }
      };

      this.socketService.on('get_user_profile_response', handler);
      this.socketService.emit('get_user_profile', { phone: cleanPhone });

      setTimeout(() => {
        this.socketService.off('get_user_profile_response', handler);
        reject(new Error('Kullanıcı profil servisi yanıt vermedi. (Zaman aşımı)'));
      }, 5000);
    });
  }

  /**
   * Retrieves user profile data from the shared server.
   */
  /**
   * Retrieves user profile data from CouchDB users database or backend server.
   */
  async getUserData(phone: string): Promise<any> {
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);

    // 1. Direct CouchDB users query with standard Basic Auth header
    if (environment.COUCHDB_BASE_URL) {
      try {
        const testIds = [`%2B90${cleanPhone}`, `+90${cleanPhone}`, cleanPhone];
        for (const id of testIds) {
          const couchUrl = `${environment.COUCHDB_BASE_URL}/users/${id}`;
          const res = await fetch(couchUrl, {
            headers: {
              'Authorization': environment.COUCHDB_AUTH_HEADER,
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(4000),
          }).catch(() => null);

          if (res && res.ok) {
            const doc = await res.json();
            if (doc && (doc.name || doc.Organization || doc.VKN || doc.vkn || doc.TCKN)) {
              console.log('[AUTH] Real user found in CouchDB users database:', doc.name || doc.Organization);
              const realVkn = doc.VKN || doc.vkn || doc.TCKN || '';
              return {
                status: 'Success',
                user: {
                  name: doc.name || doc.Organization || 'Kullanıcı',
                  phone: doc.phone || `+90${cleanPhone}`,
                  taxid: realVkn,
                  vkn: realVkn,
                  organization: doc.Organization,
                  plan: doc.plan || 'pro',
                  email: doc.email,
                  userType: doc.userType || 'SMMM',
                  userRole: doc.userType || doc.role || 'SMMM',
                },
              };
            }
          }
        }
      } catch (couchErr) {
        console.warn('[AUTH] CouchDB direct user check error:', couchErr);
      }
    }

    // 2. Check /api/v1/user-data endpoint on apiBaseUrl or remote VPS host
    const endpoints = [
      `${environment.apiBaseUrl}/api/v1/user-data`,
    ];

    for (const url of endpoints) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: cleanPhone }),
          signal: AbortSignal.timeout(4000),
        });

        if (response.ok) {
          const result = await response.json();
          if (result && result.user) {
            console.log(`[AUTH] Real user data retrieved from ${url}:`, result.user.name);
            return result;
          }
        }
      } catch (err) {
        // continue to next endpoint
      }
    }

    return {
      status: 'NOT_FOUND',
      message: 'Kullanıcı veritabanında bulunamadı.',
    };
  }

  /**
   * One-time direct token login (bypasses login and SMS screen)
   */
  async loginWithToken(token: string): Promise<boolean> {
    if (!token) return false;
    try {
      // 1. Direct CouchDB user_tokens query
      let tokenDoc: any = null;
      if (environment.COUCHDB_BASE_URL) {
        const couchUrl = `${environment.COUCHDB_BASE_URL}/user_tokens/${encodeURIComponent(token)}`;
        const res = await fetch(couchUrl, {
          headers: {
            'Authorization': environment.COUCHDB_AUTH_HEADER,
            'Accept': 'application/json',
          },
          signal: AbortSignal.timeout(5000),
        }).catch(() => null);

        if (res && res.ok) {
          tokenDoc = await res.json();
        }
      }

      // 2. Fallback backend endpoint check
      if (!tokenDoc) {
        const apiRes = await fetch(`${environment.apiBaseUrl}/api/auth/token-login?token=${encodeURIComponent(token)}`).catch(() => null);
        if (apiRes && apiRes.ok) {
          const apiData = await apiRes.json();
          tokenDoc = apiData.tokenDoc || apiData;
        }
      }

      if (!tokenDoc || tokenDoc.isValid === false) {
        console.warn('[AUTH] Token not found or marked invalid');
        return false;
      }

      // Check expiration
      if (tokenDoc.expiresAt && new Date(tokenDoc.expiresAt).getTime() < Date.now()) {
        console.warn('[AUTH] Token expired');
        return false;
      }

      const phone = tokenDoc.phone || '';
      let userObj: User | null = null;

      if (phone) {
        const userData = await this.getUserData(phone);
        if (userData && userData.user) {
          userObj = userData.user;
        }
      }

      if (!userObj) {
        userObj = {
          name: tokenDoc.name || 'Yetkili Kullanıcı',
          phone: tokenDoc.phone || '',
          vkn: tokenDoc.vkn || '',
          taxid: tokenDoc.vkn || '',
          type: tokenDoc.userType || 'Mükellef',
          userType: tokenDoc.userType || 'Mükellef',
          organization: tokenDoc.name || 'USTAT Kullanıcısı',
          plan: 'free',
          datasource: 'central_drive',
          ocr: 'gemini',
        };
      }

      this.setCurrentUser(userObj);
      console.log(`[AUTH] ✅ Token authentication successful for ${userObj.name} (${userObj.phone})`);
      return true;
    } catch (err) {
      console.error('[AUTH] Token login error:', err);
      return false;
    }
  }

  /**
   * One-time direct login using phone from WhatsApp link.
   * Checks CouchDB 'ocr_users' first, then CouchDB 'users', extracts VKN and user profile, and skips OTP.
   */
  async loginWithPhoneDirect(rawPhone: string, token?: string): Promise<boolean> {
    if (!rawPhone) return false;
    const digits = rawPhone.replace(/\D/g, '');
    const tenDigit = digits.slice(-10);
    if (tenDigit.length < 10) return false;
    const formattedPhone = '+90' + tenDigit;

    console.log(`[DIRECT_LOGIN] Checking phone across ocr_users and users: ${formattedPhone}`);

    let ocrDoc: any = null;
    let userDoc: any = null;

    const candidateIds = [
      encodeURIComponent(formattedPhone),
      formattedPhone,
      `90${tenDigit}`,
      tenDigit,
    ];

    // 1. Primary: Check CouchDB ocr_users
    if (environment.COUCHDB_BASE_URL) {
      for (const id of candidateIds) {
        try {
          const res = await fetch(`${environment.COUCHDB_BASE_URL}/ocr_users/${id}`, {
            headers: {
              'Authorization': environment.COUCHDB_AUTH_HEADER,
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(4000),
          });
          if (res.ok) {
            const data = await res.json();
            if (data && (data.phone || data.name || data.uploadedVergiLevhasi)) {
              ocrDoc = data;
              console.log(`[DIRECT_LOGIN] ✅ User found in ocr_users:`, ocrDoc.name || ocrDoc.phone);
              break;
            }
          }
        } catch (_) {}
      }
    }

    // 2. Secondary: Check CouchDB users
    if (environment.COUCHDB_BASE_URL) {
      for (const id of candidateIds) {
        try {
          const res = await fetch(`${environment.COUCHDB_BASE_URL}/users/${id}`, {
            headers: {
              'Authorization': environment.COUCHDB_AUTH_HEADER,
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(4000),
          });
          if (res.ok) {
            const data = await res.json();
            if (data && (data.phone || data.name || data.Organization || data.VKN || data.TCKN)) {
              userDoc = data;
              console.log(`[DIRECT_LOGIN] ✅ User found in users:`, userDoc.name || userDoc.Organization, '| VKN:', userDoc.VKN || userDoc.TCKN);
              break;
            }
          }
        } catch (_) {}
      }
    }

    // 3. Fallback: query backend /api/v1/user-data or check token
    if (!ocrDoc && !userDoc) {
      try {
        const backendRes = await this.getUserData(tenDigit);
        if (backendRes && backendRes.user) {
          userDoc = backendRes.user;
        }
      } catch (_) {}
    }

    // If token is provided and user still not found, try token fallback
    if (!ocrDoc && !userDoc && token) {
      return await this.loginWithToken(token);
    }

    if (!ocrDoc && !userDoc) {
      console.warn(`[DIRECT_LOGIN] ⚠️ Phone not found in ocr_users or users: ${formattedPhone}`);
      return false;
    }

    // Determine VKN, name, organization, userType
    const finalVkn = userDoc?.VKN || userDoc?.vkn || userDoc?.TCKN || userDoc?.taxid || ocrDoc?.vkn || ocrDoc?.uploadedVergiLevhasi?.vkn || '7440496938';
    const finalName = userDoc?.name || userDoc?.Organization || ocrDoc?.name || 'Mükellef';
    const finalOrg = userDoc?.Organization || ocrDoc?.companyName || userDoc?.name || 'USTAT Mükellefi';
    const finalType = userDoc?.userType || userDoc?.role || ocrDoc?.userType || 'Mükellef';
    const finalPlan = userDoc?.plan || 'free';
    const finalEmail = userDoc?.email || ocrDoc?.email || '';

    const userObj: User = {
      name: finalName,
      phone: formattedPhone,
      vkn: finalVkn,
      taxid: finalVkn,
      taxId: finalVkn,
      companyName: finalOrg,
      organization: finalOrg,
      type: finalType,
      userType: finalType,
      userRole: finalType,
      plan: finalPlan,
      email: finalEmail,
      datasource: userDoc?.datasource || 'central_drive',
      ocr: userDoc?.ocr || 'gemini',
      status: 'active',
    };

    this.setCurrentUser(userObj);
    console.log(`[DIRECT_LOGIN] 🚀 Direct session established without OTP:`, userObj.name, userObj.vkn);
    return true;
  }


  async logout(): Promise<void> {
    try {
      await this.firebaseAuth.signOut();
    } catch (error) {
      console.error('[AUTH ERROR] Firebase signOut failed:', error);
    } finally {
      this.setCurrentUser(null);
      if (typeof window !== 'undefined') {
        try {
          // Clear all Firebase auth items, ustat_* items and user session from localStorage
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (
              key &&
              (key.startsWith('firebase:') ||
                key.includes('firebase') ||
                key.startsWith('ustat_') ||
                key === 'currentUser' ||
                key === 'user' ||
                key === 'google_access_token')
            ) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((k) => localStorage.removeItem(k));

          // Clear sessionStorage
          window.sessionStorage?.clear();

          // Delete Firebase IndexedDB cache completely to prevent background token resurrection
          if (window.indexedDB) {
            window.indexedDB.deleteDatabase('firebaseLocalStorageDb');
          }
          console.log('[AUTH] All Firebase credentials and session tokens cleared. PouchDB data preserved on device.');
        } catch (_) {}
      }
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = '/';
      } else {
        this.router.navigate(['/login']);
      }
    }
  }
}
